import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setCsrfToken, setOnUnauthorized, type MeResponse } from "../api/client";
import { keys } from "../api/query-registry";

// Session hooks — own the ["me"] cache entry and the CSRF token side effect so
// pages/components never touch the query client directly.

export function useMe() {
  return useQuery({
    queryKey: keys.me(),
    queryFn: async () => {
      const me = await api<MeResponse>("/api/auth/me");
      setCsrfToken(me.csrfToken);
      return me;
    },
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api<MeResponse>("/api/auth/login", { method: "POST", body }),
    onSuccess: (res) => {
      setCsrfToken(res.csrfToken);
      qc.setQueryData(keys.me(), res);
    },
  });
}

/** Logs out and clears the whole cache; callers navigate to /login. */
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/api/auth/logout", { method: "POST" }),
    // Clear even when the request fails — a dead session must not keep data warm.
    onSettled: () => {
      setCsrfToken("");
      qc.clear();
    },
  });
}

/** Mount once (in App) to wire the api client's 401 handler to the session */
// cache: an expired cookie mid-use must drop the cached identity so RequireAuth
// refetches /api/auth/me and redirects to /login.
export function useSessionExpiryGuard(): void {
  const qc = useQueryClient();
  useEffect(() => {
    setOnUnauthorized(() => {
      setCsrfToken("");
      qc.removeQueries({ queryKey: keys.me() });
    });
    return () => setOnUnauthorized(null);
  }, [qc]);
}

